import request from 'api/requestUtils'
import qs from 'qs'

interface ServerRefundInfo {
  item_metadata: {
    charge_component_key: string;
    cash_amount: number;
    accounting_amount: number;
    promo_amount: number;
  };
  surcharge_metadata?: {
    charge_component_key: string;
    cash_amount: number;
    accounting_amount: number;
  };
  business_credit_refund_amount: number;
  merchant_fee_amount?: number;
  is_partially_paid_order: boolean;
  bonus_credit: number;
  insurance_metadata: {
    is_refundable: boolean;
    accounting_amount: number;
    cancel_insurance: boolean;
  };
}

interface Params {
  businessId: string;
  orderId: string;
  itemId: string;
  roomId?: string;
}

/**
 * Fetches refund preview through svc-business proxy for business traveller orders.
 * BT equivalent of getPossibleRoomRefundTotal in api/order.ts.
 * The proxy calculates and injects business_credit_refund_amount.
 */
async function requestGetRefundPreview(params: Params): Promise<App.RefundInfo> {
  const { businessId, orderId, itemId, roomId } = params
  const queryParams = qs.stringify({ room_id: roomId, refund_type: 'auto' })

  try {
    const response = await request.get<App.ApiResponse<ServerRefundInfo>>(
      `/api/business-traveller/businesses/${businessId}/orders/${orderId}/order-items/${itemId}/refund?${queryParams}`,
      { credentials: 'include' },
    )

    const result = response.result
    const willRefundInsurance = result.insurance_metadata.cancel_insurance && result.insurance_metadata.is_refundable
    const itemRefundAmount = result.is_partially_paid_order ? result.item_metadata.cash_amount : result.item_metadata.accounting_amount
    const surchargeRefundAmount = result.surcharge_metadata?.accounting_amount || 0
    const merchantFeeRefundAmount = result.merchant_fee_amount || 0
    const promoRefundAmount = result.item_metadata?.promo_amount || 0
    const refundableAmount = itemRefundAmount + surchargeRefundAmount + merchantFeeRefundAmount - promoRefundAmount

    return {
      refundableAmount,
      businessCreditRefundableAmount: result.business_credit_refund_amount || 0,
      refundableMerchantFeeAmount: result.merchant_fee_amount || 0,
      bonusCreditAmount: result.bonus_credit || 0,
      promoAmount: result.item_metadata.promo_amount || 0,
      insurance: {
        willCancel: result.insurance_metadata.cancel_insurance ?? false,
        isRefundable: result.insurance_metadata.is_refundable ?? false,
        refundAmount: willRefundInsurance ? result.insurance_metadata.accounting_amount ?? 0 : 0,
      },
    }
  } catch {
    return {
      refundableAmount: 0,
      businessCreditRefundableAmount: 0,
      refundableMerchantFeeAmount: 0,
      bonusCreditAmount: 0,
      promoAmount: 0,
      insurance: {
        willCancel: false,
        isRefundable: false,
        refundAmount: 0,
      },
    }
  }
}

export default requestGetRefundPreview
